import { message } from 'antd'
import { useCallback, useState } from 'react'
import { useAccount, useSwitchChain } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { SettingEnum } from 'constants/setting'
import { VestingTypeEnum } from 'constants/vesting'
import { useAppSetting } from 'hooks/setting/use-app-setting'
import { initSafeClients, SafeClients } from 'libs/safe/client'
import { parseSafeError } from 'libs/safe/errors'
import { getPendingBatches, PendingBatch } from 'libs/safe/get-pending-batches'
import { proposeBatchTransaction, ProposedBatchResult } from 'libs/safe/propose-batch'
import { publicWallet } from 'libs/viem/config'
import {
  FlattenedEntry,
  mapNotImportedToEntries,
} from 'modules/vesting-v2/map-not-imported-to-entries'
import { logError, logInfo } from 'utils/logger'

const BATCH_SIZE = 100

export interface ProposedBatch extends ProposedBatchResult {
  batchIndex: number
  entriesCount: number
}

interface UseImportVestingMultisigParams {
  type: VestingTypeEnum | undefined
  vestingContractAddress: `0x${string}` | undefined
}

interface FailedBatchInfo {
  batchIndex: number
  entriesCount: number
  message: string
}

export const useImportVestingMultisig = ({
  type,
  vestingContractAddress,
}: UseImportVestingMultisigParams) => {
  const { address, chainId: walletChainId } = useAccount()
  const { switchChainAsync } = useSwitchChain()
  const { getSetting } = useAppSetting()

  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [processed, setProcessed] = useState(0)
  const [total, setTotal] = useState(0)
  const [proposedBatches, setProposedBatches] = useState<ProposedBatch[]>([])
  const [failedBatch, setFailedBatch] = useState<FailedBatchInfo | null>(null)
  const [pendingEntries, setPendingEntries] = useState<FlattenedEntry[]>([])
  const [safeAddressState, setSafeAddressState] = useState<`0x${string}` | null>(null)
  const [clientsState, setClientsState] = useState<SafeClients | null>(null)
  const [lastNonce, setLastNonce] = useState<number | null>(null)
  const [viewingPending, setViewingPending] = useState(false)
  const [pendingBatches, setPendingBatches] = useState<PendingBatch[]>([])
  const [fetchingPending, setFetchingPending] = useState(false)

  const resetState = useCallback(() => {
    setOpen(false)
    setProcessed(0)
    setTotal(0)
    setProposedBatches([])
    setFailedBatch(null)
    setPendingEntries([])
    setSafeAddressState(null)
    setClientsState(null)
    setLastNonce(null)
    setViewingPending(false)
    setPendingBatches([])
    setFetchingPending(false)
  }, [])

  const runBatches = useCallback(
    async (
      entries: FlattenedEntry[],
      startingBatchIndex: number,
      clients: SafeClients,
      safeAddress: `0x${string}`,
      signerAddress: `0x${string}`,
      vestingAddress: `0x${string}`,
      initialNonce: number | null
    ) => {
      let previousNonce = initialNonce

      for (let i = 0; i < entries.length; i += BATCH_SIZE) {
        const chunkEntries = entries.slice(i, i + BATCH_SIZE)
        const batchIndex = startingBatchIndex + Math.floor(i / BATCH_SIZE)

        const chunk = {
          users: chunkEntries.map((e) => e.user),
          signDates: chunkEntries.map((e) => e.signDateSec),
          amounts: chunkEntries.map((e) => e.amountWei),
        }

        try {
          const result = await proposeBatchTransaction({
            clients,
            safeAddress,
            signerAddress,
            vestingContractAddress: vestingAddress,
            chunk,
            nonce: previousNonce !== null ? previousNonce + 1 : undefined,
          })

          previousNonce = result.nonce
          setLastNonce(result.nonce)

          setProposedBatches((prev) => [
            ...prev,
            {
              ...result,
              batchIndex,
              entriesCount: chunkEntries.length,
            },
          ])
          setProcessed((prev) => prev + chunkEntries.length)
        } catch (error) {
          logError('proposeBatchTransaction failed', error)
          setFailedBatch({
            batchIndex,
            entriesCount: chunkEntries.length,
            message: parseSafeError(error),
          })
          setPendingEntries(entries.slice(i))
          return
        }
      }

      setPendingEntries([])
      setFailedBatch(null)
    },
    []
  )

  const importWhitelist = useCallback(async () => {
    if (!type) return
    if (!vestingContractAddress) {
      message.error('Vesting contract is not ready')
      return
    }
    if (!address) {
      message.error('Please connect wallet')
      return
    }

    setLoading(true)

    try {
      const targetChainId = publicWallet.chain.id
      if (walletChainId !== targetChainId) {
        try {
          await switchChainAsync({ chainId: targetChainId })
        } catch (error) {
          message.error(parseSafeError(error))
          setLoading(false)
          return
        }
      }

      const res = await AppAdmin.getVestingNotImported(type)
      if (res?.data?.code !== 200) {
        message.error(res?.data?.message ?? 'Failed to fetch whitelist')
        setLoading(false)
        return
      }

      const notImportedList = res?.data?.data ?? []
      if (!notImportedList.length) {
        message.info('No data to import')
        setLoading(false)
        return
      }

      const entries = mapNotImportedToEntries(notImportedList)
      if (entries.length === 0) {
        message.info('No valid entries to import')
        setLoading(false)
        return
      }

      const settingSafeAddress = getSetting(SettingEnum.SAFE_VESTING_MULTISIG_ADDRESS) as
        | `0x${string}`
        | null

      logInfo('Safe address from settings', { settingSafeAddress })
      if (!settingSafeAddress || !/^0x[a-fA-F0-9]{40}$/.test(settingSafeAddress)) {
        message.error('Safe multisig address is not configured in settings.')
        setLoading(false)
        return
      }
      const safeAddress: `0x${string}` = settingSafeAddress

      try {
        const bytecode = await publicWallet.getCode({ address: safeAddress })
        if (!bytecode || bytecode === '0x') {
          message.error('Configured Safe address is not a deployed contract on this network.')
          setLoading(false)
          return
        }
      } catch (error) {
        message.error('Failed to verify Safe contract onchain')
        logError('getCode safe failed', error)
        setLoading(false)
        return
      }

      let clients: SafeClients
      try {
        clients = await initSafeClients({
          safeAddress,
          chainId: targetChainId,
          signerAddress: address,
        })
      } catch (error) {
        message.error(parseSafeError(error))
        logError('initSafeClients failed', error)
        setLoading(false)
        return
      }

      setSafeAddressState(safeAddress)
      setClientsState(clients)
      setProposedBatches([])
      setFailedBatch(null)
      setProcessed(0)
      setTotal(entries.length)
      setOpen(true)

      await runBatches(entries, 0, clients, safeAddress, address, vestingContractAddress, null)
    } finally {
      setLoading(false)
    }
  }, [
    type,
    vestingContractAddress,
    address,
    walletChainId,
    switchChainAsync,
    runBatches,
    getSetting,
  ])

  const retry = useCallback(async () => {
    if (!pendingEntries.length || !clientsState || !safeAddressState || !address) {
      return
    }
    if (!vestingContractAddress) return
    if (!failedBatch) return

    setLoading(true)
    setFailedBatch(null)
    try {
      await runBatches(
        pendingEntries,
        failedBatch.batchIndex,
        clientsState,
        safeAddressState,
        address,
        vestingContractAddress,
        lastNonce
      )
    } finally {
      setLoading(false)
    }
  }, [
    pendingEntries,
    clientsState,
    safeAddressState,
    address,
    vestingContractAddress,
    failedBatch,
    lastNonce,
    runBatches,
  ])

  const viewPending = useCallback(async () => {
    if (!vestingContractAddress) {
      message.error('Vesting contract is not ready')
      return
    }

    const settingSafeAddress = getSetting(SettingEnum.SAFE_VESTING_MULTISIG_ADDRESS) as
      | `0x${string}`
      | null
    if (!settingSafeAddress || !/^0x[a-fA-F0-9]{40}$/.test(settingSafeAddress)) {
      message.error('Safe multisig address is not configured in settings.')
      return
    }

    setPendingBatches([])
    setViewingPending(true)
    setFetchingPending(true)
    setOpen(true)

    try {
      const batches = await getPendingBatches({
        safeAddress: settingSafeAddress,
        chainId: publicWallet.chain.id,
        vestingContractAddress,
      })
      setPendingBatches(batches)
    } catch (error) {
      logError('getPendingBatches failed', error)
      message.error(parseSafeError(error))
      setOpen(false)
      setViewingPending(false)
    } finally {
      setFetchingPending(false)
    }
  }, [vestingContractAddress, getSetting])

  const close = useCallback(() => {
    resetState()
  }, [resetState])

  return {
    importWhitelist,
    retry,
    close,
    viewPending,
    loading,
    open,
    processed,
    total,
    proposedBatches,
    failedBatch,
    viewingPending,
    pendingBatches,
    fetchingPending,
  }
}
